import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useGetMembersQuery } from "../../features/members/membersApiSlice";
import { useGetBankingAccountsQuery } from "../../features/banking/bankingApiSlice";
import {
  useGetEventQuery,
  useGraduateAttendeesMutation,
  useDeleteEventMutation,
} from "../../features/events/eventsApiSlice";
import { useCreateBookingMutation } from "../../features/banking/bookingsApiSlice";
import { useGetTenantInformationQuery } from "../../features/tenants/tenantsApiSlice";
import { useDispatch } from "react-redux";
import { setDialog } from "../../features/dialog/dialogSlice";
import FeeCollectionForm from "../../sections/@dashboard/banking/FeeCollectionForm";
import { getTestingFeeByMemberId } from "../../utils/testingFeeUtils";

const getId = (obj) => String(obj?._id || obj?.id || obj || "");

const buildTestingReference = (event) => {
  if (!event) return "";
  const date = event?.eventDateBegin
    ? dayjs(event.eventDateBegin).format("DD.MM.YYYY")
    : "";
  return ["Prüfungsgebühr", event?.eventName, date]
    .filter(Boolean)
    .join(" ")
    .slice(0, 140);
};

export default function TestingFeeCollectionDialog({ open, onClose, event }) {
  const dispatch = useDispatch();
  const eventId = getId(event);
  const { data: eventDetail, isFetching: loadingEvent } = useGetEventQuery(
    eventId,
    { skip: !eventId },
  );
  const { data: members = [], isLoading: loadingMembers } =
    useGetMembersQuery();
  const { data: accounts = [], isLoading: loadingAccounts } =
    useGetBankingAccountsQuery();
  const { data: tenantInfo } = useGetTenantInformationQuery();
  const [createBooking] = useCreateBookingMutation();
  const [graduateAttendees] = useGraduateAttendeesMutation();
  const [deleteEvent] = useDeleteEventMutation();

  const [selectedAccountId, setSelectedAccountId] = useState("");

  useEffect(() => {
    if (!open || !accounts.length) return;
    const main = accounts.find((acc) => acc.isMain);
    const nextId = getId(main || accounts[0]);
    setSelectedAccountId(nextId);
  }, [open, accounts]);

  const selectedAccount = useMemo(
    () => accounts.find((acc) => getId(acc) === selectedAccountId) || null,
    [accounts, selectedAccountId],
  );

  const creditor = useMemo(() => {
    if (!selectedAccount) return null;
    return {
      creditorName: selectedAccount?.creditorName || "—",
      creditorIBAN: selectedAccount?.creditorIBAN || "",
      creditorBIC: selectedAccount?.creditorBIC || "",
      creditorId: selectedAccount?.creditorId || "",
      remittanceInfo: selectedAccount?.remittanceInfo || "",
    };
  }, [selectedAccount]);

  const attendeeIds = useMemo(() => {
    const list =
      eventDetail?.hasAttendees || event?.hasAttendees || event?.attendees || [];
    return list.map((item) => getId(item)).filter(Boolean);
  }, [eventDetail, event]);

  const attendeeMembers = useMemo(() => {
    if (!attendeeIds.length) return [];
    const idSet = new Set(attendeeIds);
    return members.filter((m) => idSet.has(getId(m)));
  }, [members, attendeeIds]);

  const testingFeeByMemberId = useMemo(() => {
    const fees = tenantInfo?.settings?.testingSettings?.fees || {};
    return getTestingFeeByMemberId(attendeeMembers, fees);
  }, [tenantInfo, attendeeMembers]);

  const mergedEvent = eventDetail || event;
  const eventLabel = mergedEvent?.eventName || "Prüfung";
  const referenceText = useMemo(
    () => buildTestingReference(eventDetail || event),
    [eventDetail, event],
  );

  const hasAttendees = attendeeIds.length > 0;
  const showForm = !!eventId && !!creditor && hasAttendees;
  const isBusy = loadingEvent || loadingMembers || loadingAccounts;

  const handleTestingExport = async (xml, summary = {}) => {
    const {
      totalAmount,
      collectionDate,
      memberIds = [],
      membersCount: membersCountOverride,
      siteIds = [],
      transactions,
      referenceText,
    } = summary;
    const membersCount =
      Number.isFinite(Number(membersCountOverride)) && membersCountOverride >= 0
        ? Number(membersCountOverride)
        : new Set(memberIds.filter(Boolean)).size;
    if (!collectionDate || !Number.isFinite(Number(totalAmount))) return;

    try {
      await createBooking({
        bookingType: "testing",
        bookingDate: collectionDate,
        membersCount,
        totalSum: Number(totalAmount),
        referenceText,
        transactionsCount: transactions,
        siteIds,
      }).unwrap();
    } catch (error) {
      console.error("Failed to create booking:", error);
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Fehler",
          data: {
            errorText:
              error?.data?.message ||
              "Die Buchung konnte nicht gespeichert werden.",
          },
        })
      );
    }
  };

  const handleGraduateTestingAttendees = async () => {
    if (!eventId) return;
    try {
      await graduateAttendees(eventId).unwrap();
    } catch (error) {
      console.error("Failed to graduate testing attendees:", error);
    }
  };

  const handleDeleteTestingList = async () => {
    if (!eventId) return;
    try {
      await deleteEvent(eventId).unwrap();
      onClose?.();
    } catch (error) {
      console.error("Failed to delete testing list:", error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Einzug Prüfungsgebühr</DialogTitle>
      <DialogContent dividers>
        {isBusy && <LinearProgress sx={{ mb: 2 }} />}
        {!eventId && (
          <Typography variant="body2" color="text.secondary">
            Keine Prüfung ausgewählt.
          </Typography>
        )}

        {!!eventId && (
          <Stack spacing={2} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="subtitle1">{eventLabel}</Typography>
              {mergedEvent?.eventDateBegin && (
                <Typography variant="body2" color="text.secondary">
                  Datum: {dayjs(mergedEvent.eventDateBegin).format("DD.MM.YYYY")}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                Teilnehmer: {attendeeIds.length}
              </Typography>
            </Box>

            <FormControl sx={{ maxWidth: 420 }}>
              <InputLabel id="testing-fee-account-label">
                Konto für SEPA-Einzug
              </InputLabel>
              <Select
                labelId="testing-fee-account-label"
                label="Konto für SEPA-Einzug"
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
              >
                {accounts.map((acc) => {
                  const id = getId(acc);
                  return (
                    <MenuItem key={id} value={id}>
                      {acc.creditorName || "—"} ({acc.creditorIBAN || "—"})
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Stack>
        )}

        {!!eventId && accounts.length === 0 && !loadingAccounts && (
          <Typography variant="body2" color="text.secondary">
            Keine Bankkonten vorhanden. Bitte hinterlegen Sie ein Konto in den
            Einstellungen.
          </Typography>
        )}

        {!!eventId &&
          accounts.length > 0 &&
          !selectedAccountId &&
          !loadingAccounts && (
            <Typography variant="body2" color="text.secondary">
              Bitte wählen Sie ein Konto für den SEPA-Einzug aus.
            </Typography>
          )}

        {!!eventId && !hasAttendees && !loadingEvent && (
          <Typography variant="body2" color="text.secondary">
            Für diese Prüfung sind keine Teilnehmer hinterlegt.
          </Typography>
        )}

        {showForm && (
          <FeeCollectionForm
            key={`${eventId}-${attendeeIds.length}-${selectedAccountId}`}
            members={attendeeMembers}
            creditor={creditor}
            loading={loadingMembers}
            title="SEPA-Lastschrift (Prüfungsgebühr)"
            description="Es werden nur Teilnehmer der ausgewählten Prüfung angezeigt."
            defaultReferenceText={referenceText}
            defaultOnlyActive={false}
            defaultSelectAll
            defaultFeeByMemberId={testingFeeByMemberId}
            showTestingActions
            onGraduateMembers={handleGraduateTestingAttendees}
            onDeleteTestingList={handleDeleteTestingList}
            onExport={handleTestingExport}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="info">
          Schließen
        </Button>
      </DialogActions>
    </Dialog>
  );
}

TestingFeeCollectionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  event: PropTypes.object,
};
